import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { User, Lock, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ErrorMessage } from '@/components/ui/error-message';
import { useAuthStore } from '@/store/authStore';
import { authApi } from '@/api/auth';
import Routes from '@/router/routes';
import TelletLogo from '@/assets/logo/tellet-logo-main.svg';
import { nameSchema, passwordSchema, confirmPasswordSchema } from '@/lib/validationSchemas';

const validationSchema = Yup.object().shape({
  name: nameSchema,
  password: passwordSchema,
  confirmPassword: confirmPasswordSchema('password'),
});

type FormData = Yup.InferType<typeof validationSchema>;

const defaultValues: FormData = {
  name: '',
  password: '',
  confirmPassword: '',
};

type PageState = 'loading' | 'invalid' | 'form' | 'submitting' | 'success';

export default function AcceptInvite() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { setSetupToken } = useAuthStore();

  const [pageState, setPageState] = useState<PageState>('loading');
  const [email, setEmail] = useState('');
  const [apiError, setApiError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(validationSchema),
    defaultValues,
  });

  useEffect(() => {
    if (!token) {
      setPageState('invalid');
      setApiError('No invitation token provided.');
      return;
    }

    const validateToken = async () => {
      try {
        const result = await authApi.validateInvite(token);
        setEmail(result.email);
        setPageState('form');
      } catch (err: any) {
        setPageState('invalid');
        setApiError(err.response?.data?.message || 'Invalid or expired invitation.');
      }
    };

    validateToken();
  }, [token]);

  const onFormSubmit = async (data: FormData) => {
    setApiError(null);

    if (!token) return;

    setPageState('submitting');

    try {
      const result = await authApi.acceptInvite(token, data.name.trim(), data.password);
      setSetupToken(result.setupToken);
      setPageState('success');
      navigate(Routes.two_factor_setup.path);
    } catch (err: any) {
      setPageState('form');
      setApiError(err.response?.data?.message || 'Failed to accept invitation. Please try again.');
    }
  };

  if (pageState === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Validating invitation...</p>
        </div>
      </div>
    );
  }

  if (pageState === 'invalid') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <img src={TelletLogo} alt="Tellet" className="mx-auto h-12 w-auto dark:invert" />
            <div className="mt-6 flex justify-center">
              <div className="rounded-full bg-red-100 dark:bg-red-900/30 p-3">
                <AlertCircle size={32} className="text-red-600 dark:text-red-400" />
              </div>
            </div>
            <h2 className="mt-4 text-2xl font-bold text-foreground">Invalid Invitation</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              {apiError || 'This invitation link is invalid or has expired.'}
            </p>
            <p className="mt-4 text-sm text-muted-foreground">
              Please contact your administrator to request a new invitation.
            </p>
          </div>
          <Button className="w-full" onClick={() => navigate(Routes.login.path)}>
            Go to Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <img src={TelletLogo} alt="Tellet" className="mx-auto h-12 w-auto dark:invert" />
          <h2 className="mt-6 text-3xl font-bold text-foreground">Complete Your Account</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Set up your super admin account for{' '}
            <span className="font-medium text-foreground">{email}</span>
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onFormSubmit)}>
          <div className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-foreground mb-1">
                Full Name
              </label>
              <Input
                id="name"
                type="text"
                autoComplete="name"
                placeholder="John Doe"
                {...register('name')}
                icon={<User size={20} />}
                hasError={!!errors.name}
              />
              <ErrorMessage message={errors.name?.message} />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-foreground mb-1">
                Password
              </label>
              <Input
                id="password"
                type="password"
                autoComplete="new-password"
                placeholder="Minimum 8 characters"
                {...register('password')}
                icon={<Lock size={20} />}
                hasError={!!errors.password}
              />
              <ErrorMessage message={errors.password?.message} />
            </div>

            <div>
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-medium text-foreground mb-1"
              >
                Confirm Password
              </label>
              <Input
                id="confirmPassword"
                type="password"
                autoComplete="new-password"
                placeholder="Re-enter your password"
                {...register('confirmPassword')}
                icon={<Lock size={20} />}
                hasError={!!errors.confirmPassword}
              />
              <ErrorMessage message={errors.confirmPassword?.message} />
            </div>
          </div>

          {apiError && (
            <div className="rounded-md bg-red-50 dark:bg-red-900/30 p-4">
              <p className="text-sm text-red-700 dark:text-red-400">{apiError}</p>
            </div>
          )}

          <div className="bg-blue-50 dark:bg-blue-900/30 rounded-md p-4">
            <p className="text-sm text-blue-700 dark:text-blue-400">
              After completing this form, you'll be required to set up two-factor authentication for
              enhanced security.
            </p>
          </div>

          <Button type="submit" className="w-full" disabled={pageState === 'submitting'}>
            {pageState === 'submitting' ? 'Creating Account...' : 'Create Account'}
          </Button>
        </form>
      </div>
    </div>
  );
}
